/**
 * Contrato de ownership para operações críticas (flush, replay, recovery).
 */

import { isLeaderTab } from '../tabCoordinator';
import {
  hasValidFlushLock,
  validateFencingToken,
  getFlushLockFencingToken,
} from './distributedLock';
import { getCoordinationEpoch, isEpochStale } from './coordinationEpoch';
import { getMinLeaderFencingToken } from '../tabCoordinator';
import { getTabId } from '../tabCoordinator';
import {
  isOwnerInvalidated,
  isActiveOperationsInvalidated,
} from './ownershipInvalidation';
import {
  recordFencingRejection,
  recordEpochInvalidation,
  recordOwnershipDenied,
} from '../telemetry';
import { isPersistedLeader } from './distributedRecovery';

function isPersistFencingValid(ctx: OperationContext): boolean {
  if (validateFencingToken(ctx.minFencingToken)) return true;
  const tabId = getTabId();
  if (!tabId) return false;
  // Persistência da fila (compaction) não exige flush lock — só aba leader persistida.
  if (
    (ctx.op === 'compaction' || ctx.op === 'queue_mutate') &&
    isPersistedLeader(tabId)
  ) {
    return true;
  }
  return false;
}

export type CriticalOperation =
  | 'flush'
  | 'replay'
  | 'recovery'
  | 'queue_mutate'
  | 'compaction'
  | 'dead_letter';

export interface OperationContext {
  op: CriticalOperation;
  minFencingToken: number;
  epochAtAcquire: number;
}

export function acquireOperationContext(op: CriticalOperation): OperationContext {
  const lockToken = getFlushLockFencingToken();
  const leaderMin = getMinLeaderFencingToken();
  return {
    op,
    minFencingToken: Math.max(lockToken, leaderMin),
    epochAtAcquire: getCoordinationEpoch(),
  };
}

export type OwnershipRejectReason =
  | 'epoch_stale'
  | 'fencing_invalid'
  | 'not_leader'
  | 'no_flush_lock'
  | 'owner_invalidated';

export function rejectIfInvalid(ctx: OperationContext): {
  ok: boolean;
  reason?: OwnershipRejectReason;
} {
  if (isEpochStale(ctx)) {
    recordEpochInvalidation();
    return { ok: false, reason: 'epoch_stale' };
  }
  if (!isPersistFencingValid(ctx)) {
    recordFencingRejection();
    return { ok: false, reason: 'fencing_invalid' };
  }
  const tabId = getTabId();
  if (isActiveOperationsInvalidated(tabId)) {
    recordOwnershipDenied();
    return { ok: false, reason: 'owner_invalidated' };
  }
  if (
    isOwnerInvalidated(tabId, ctx.epochAtAcquire, ctx.minFencingToken)
  ) {
    recordOwnershipDenied();
    return { ok: false, reason: 'owner_invalidated' };
  }
  if (ctx.op === 'flush' || ctx.op === 'replay') {
    if (!isLeaderTab()) {
      recordOwnershipDenied();
      return { ok: false, reason: 'not_leader' };
    }
    if (!hasValidFlushLock()) {
      recordOwnershipDenied();
      return { ok: false, reason: 'no_flush_lock' };
    }
  }
  if (ctx.op === 'recovery' && !isLeaderTab()) {
    recordOwnershipDenied();
    return { ok: false, reason: 'not_leader' };
  }
  return { ok: true };
}

export function assertOperationValid(ctx: OperationContext): boolean {
  return rejectIfInvalid(ctx).ok;
}
